import React, { type ReactNode } from 'react';
import { Pressable, StyleSheet, View, type ViewProps } from 'react-native';
import { Colors } from '../constants/Colors';
import { useDynamicStyles } from '../hooks';
import { isNative, type WithResponsive } from '../utils/responsivity';

type Props = {
  children: ReactNode;
  disabled?: boolean;
  shadowColor?: string;
  shadowOpacity?: number;
  onPress?: () => void;
  activeOpacity?: number;
} & ViewProps;

export function Card({
  children,
  style,
  disabled,
  shadowColor,
  shadowOpacity,
  onPress,
  activeOpacity,
  ...otherProps
}: Props) {
  const styles = useDynamicStyles(createStyles, {
    disabled,
    shadowColor,
    shadowOpacity,
  });

  if (onPress) {
    return (
      <Pressable
        onPress={onPress}
        disabled={disabled}
        style={({ pressed }) => [
          styles.container,
          { opacity: pressed ? (activeOpacity ?? 0.8) : 1 },
          style,
        ]}
        {...otherProps}
      >
        {children}
      </Pressable>
    );
  }

  return (
    <View style={[styles.container, style]} {...otherProps}>
      {children}
    </View>
  );
}

const createStyles = ({
  isMobile,
  disabled,
  shadowColor,
  shadowOpacity,
}: WithResponsive<Props>) =>
  StyleSheet.create({
    container: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: isMobile ? 12 : 16,
      padding: isMobile ? 12 : 16,
      backgroundColor: Colors.purple0,
      borderRadius: isMobile ? 16 : 30,
      opacity: disabled ? 0.4 : undefined,
      shadowColor,
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: shadowOpacity || 0.5,
      shadowRadius: isNative ? 7.5 : 15,
      elevation: 10, // Needed for Android
    },
  });
